import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton } from '@mui/material';
import { useTranslation } from '../../hooks/useTranslation';

/** Matches `StyledPricingCard` max width + `gap` between cards */
const CARD_MAX_WIDTH_PX = 340;
const GAP_REM = 2.5;

type PricingCardsCarouselProps = {
  children: React.ReactNode;
  /** Scrolls so this plan card is visible when it would otherwise be off-screen. */
  scrollToPlanId?: string | null;
};

function isSlideFullyVisible(
  scroller: HTMLElement,
  slide: HTMLElement
): boolean {
  const scrollerRect = scroller.getBoundingClientRect();
  const slideRect = slide.getBoundingClientRect();
  const epsilon = 2;
  return (
    slideRect.left >= scrollerRect.left - epsilon &&
    slideRect.right <= scrollerRect.right + epsilon
  );
}

function scrollSlideIntoViewIfNeeded(
  scroller: HTMLElement,
  card: HTMLElement
): void {
  const slide = card.parentElement;
  if (!(slide instanceof HTMLElement)) return;
  if (isSlideFullyVisible(scroller, slide)) return;

  // Only adjust the carousel's horizontal scroll — avoid page scrollIntoView.
  const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
  const targetLeft = slideCenter - scroller.clientWidth / 2;
  const maxLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
  scroller.scrollTo({
    left: Math.min(maxLeft, Math.max(0, targetLeft)),
    behavior: 'smooth',
  });
}

const PricingCardsCarousel: React.FC<PricingCardsCarouselProps> = ({
  children,
  scrollToPlanId = null,
}) => {
  const { t } = useTranslation();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const items = React.Children.toArray(children).filter(Boolean);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const epsilon = 4;
    setCanScrollLeft(scrollLeft > epsilon);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - epsilon);
  }, []);

  useLayoutEffect(() => {
    updateScrollState();
  }, [items.length, updateScrollState]);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el || !scrollToPlanId) return;

    let settled = false;
    const settleOn = (card: HTMLElement) => {
      if (settled) return;
      settled = true;
      observer.disconnect();
      scrollSlideIntoViewIfNeeded(el, card);
      updateScrollState();
    };

    const run = (allowPlanIdFallback: boolean) => {
      if (settled) return;
      const marked = el.querySelector('[data-pricing-current-plan="true"]');
      if (marked instanceof HTMLElement) {
        settleOn(marked);
        return;
      }
      if (!allowPlanIdFallback) return;
      const byId = el.querySelector(
        `[data-plan-id="${CSS.escape(scrollToPlanId)}"]`
      );
      if (byId instanceof HTMLElement) settleOn(byId);
    };

    const observer = new MutationObserver(() => run(false));
    observer.observe(el, {
      attributes: true,
      subtree: true,
      attributeFilter: ['data-pricing-current-plan', 'data-plan-id'],
    });
    run(false);
    const timeoutId = window.setTimeout(() => run(true), 150);
    return () => {
      window.clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [scrollToPlanId, items.length, updateScrollState]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(() => updateScrollState());
    ro.observe(el);
    window.addEventListener('resize', updateScrollState);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState]);

  const scrollByOne = (direction: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el || el.children.length < 1) return;
    const first = el.children[0] as HTMLElement;
    const g = getComputedStyle(el);
    const gapStr = g.columnGap || g.gap || '0px';
    const gapPx = Number.parseFloat(gapStr) || 0;
    const w = first.offsetWidth;
    el.scrollBy({
      left: direction * (w + gapPx),
      behavior: 'smooth',
    });
  };

  if (items.length === 0) return null;

  const showArrows = items.length > 1;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        pt: { xs: 2, sm: 3 },
        pb: 0.5,
      }}
    >
      <Box
        ref={scrollerRef}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          gap: `${GAP_REM}rem`,
          overflowX: 'auto',
          scrollBehavior: 'smooth',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          // Room for card hover (translateY) + box-shadow so borders aren’t clipped by overflow
          pt: 4,
          pb: 3,
          px: { xs: 4, sm: 5 },
          boxSizing: 'border-box',
          width: '100%',
          minWidth: 0,
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {items.map((child, index) => {
          const key =
            React.isValidElement(child) && child.key != null
              ? child.key
              : `carousel-${index}`;
          return (
            <Box
              key={key}
              sx={{
                flex: '0 0 auto',
                width: CARD_MAX_WIDTH_PX,
                maxWidth: 'min(100%, 340px)',
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'stretch',
              }}
            >
              {child}
            </Box>
          );
        })}
      </Box>

      {showArrows && (
        <>
          <IconButton
            aria-label={t('Previous')}
            color="primary"
            disabled={!canScrollLeft}
            onClick={() => scrollByOne(-1)}
            size="medium"
            sx={{
              position: 'absolute',
              left: { xs: 0, sm: -12 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              width: 52,
              height: 52,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              boxShadow: 2,
              border: 'none',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
                color: 'action.disabled',
                opacity: 1,
              },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 32 }} />
          </IconButton>
          <IconButton
            aria-label={t('Next')}
            color="primary"
            disabled={!canScrollRight}
            onClick={() => scrollByOne(1)}
            size="medium"
            sx={{
              position: 'absolute',
              right: { xs: 0, sm: -12 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              width: 52,
              height: 52,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              boxShadow: 2,
              border: 'none',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
                color: 'action.disabled',
                opacity: 1,
              },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: 32 }} />
          </IconButton>
        </>
      )}
    </Box>
  );
};

export default PricingCardsCarousel;
